import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  QButton,
  QFormControl,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QStack,
  QText,
  QTextarea,
} from '@qualio/ui-components';
import { Collaborator } from '../../../../../types';

type ChangedCollaboratorsProps = {
  label: string;
  collaborators: Collaborator[];
  dataCy: string;
};

const ChangedCollaborators = ({ label, collaborators, dataCy }: ChangedCollaboratorsProps) => {
  const { t } = useTranslation();
  return (
    <QStack spacing="1" data-cy={dataCy}>
      <QText fontWeight="semibold">{label}</QText>
      {collaborators.length > 0 ? (
        collaborators.map(({ id, full_name }) => <QText key={id}>{full_name}</QText>)
      ) : (
        <QText>{t('common.labels.none')}</QText>
      )}
    </QStack>
  );
};

type ManageCollaboratorsConfirmationModalProps = {
  added: Collaborator[];
  removed: Collaborator[];
  onClose: () => void;
  onBack: () => void;
  onSubmit: (comment: string) => Promise<void>;
};

export const ManageCollaboratorsConfirmationModal = ({
  added,
  removed,
  onClose,
  onBack,
  onSubmit,
}: ManageCollaboratorsConfirmationModalProps) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);

  const applyChanges = async () => {
    setIsApplyingChanges(true);
    try {
      await onSubmit(comment.trim());
    } catch {
      setIsApplyingChanges(false);
    }
  };

  return (
    <QModal isOpen onClose={onClose}>
      <QModalHeader>
        <QText fontSize="xl">{t('events.messages.manageCollaborators')}</QText>
      </QModalHeader>
      <QModalBody>
        <QStack spacing="6">
          <ChangedCollaborators
            label={t('events.messages.collaboratorsAdded')}
            collaborators={added}
            dataCy="manage-collaborators-added"
          />
          <ChangedCollaborators
            label={t('events.messages.collaboratorsRemoved')}
            collaborators={removed}
            dataCy="manage-collaborators-removed"
          />
          <QFormControl label={t('common.labels.comment')} isRequired>
            <QTextarea
              data-cy="manage-collaborators-modal-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          </QFormControl>
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton
          data-cy="manage-collaborators-cancel-button"
          variant="ghost"
          isDisabled={isApplyingChanges}
          onClick={onClose}
        >
          {t('common.actions.cancel')}
        </QButton>
        <QButton
          data-cy="manage-collaborators-back-button"
          variant="outline"
          isDisabled={isApplyingChanges}
          onClick={onBack}
        >
          {t('common.labels.back')}
        </QButton>
        <QButton
          data-cy="manage-collaborators-apply-changes-button"
          isLoading={isApplyingChanges}
          isDisabled={!comment.trim() || isApplyingChanges}
          onClick={applyChanges}
        >
          {t('common.actions.applyChanges')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
